import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import compose from 'lodash.flowright';
import { connect } from 'react-redux';
import { graphql } from 'react-apollo';

import IntroLayoutConfigurator from '../AlbumSlideshowDesignerView/components/SecondaryIntroSlideView/components/IntroLayoutConfigurator/IntroLayoutConfigurator';
import useAlbumSlideshow from '../hooks/useAlbumSlideshow';
import { ALBUM_SLIDESHOW_MODE } from '../../../../../../constants/albums';
import FullScreenLoader from '../../../../../../components/global/FullScreenLoader';
import { isIntroLayout } from '../AlbumSlideshowDesignerView/helpers';
import { isAlbumOwner } from '../../../../../../util/account';
import { getErrorString } from '../../../../../../util/normalize-errors';
import useOpenAlert from '../../../../../../hooks/useOpenAlert';
import { withContributorPermissions } from '../../../../services/contributors';
import { MeQuery } from '../../../../../admin/graphql/queries/account';
import {
  isNativeAppWebView,
  postNativeAppAlbumIntroLayoutDesignerDismiss,
  postNativeAppAlbumIntroLayoutDesignerUpdateError,
} from '../../../../../../services/native-bridge/native-apps-interface';

const propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
  }),
  match: PropTypes.shape({
    params: PropTypes.shape({
      albumId: PropTypes.string,
    }),
  }),
};

const AlbumSlideshowIntroLayoutDesignerView = ({ user, match }) => {
  const { albumId } = match.params;
  const openAlert = useOpenAlert();
  const [saving, setSaving] = useState(false);
  const { album, slideshow, onSlideshowUpdate } = useAlbumSlideshow({
    albumId,
  });
  const isNativeView = isNativeAppWebView();
  const introLayout = slideshow?.layouts?.records?.find(isIntroLayout);
  const isOwner = isAlbumOwner(user, album);

  const onIntroLayoutUpdate = async ({ accountCard }) => {
    if (saving) {
      return;
    }

    try {
      setSaving(true);
      await onSlideshowUpdate({
        mode: ALBUM_SLIDESHOW_MODE.AUTO_LAYOUTS,
        createAutoLayouts: true,
        createLayouts: [
          {
            display_order: -1,
            account_card_id: accountCard.id,
          },
        ],
        deleteLayouts: introLayout
          ? [{ account_card_id: introLayout.account_card.id }]
          : undefined,
      });
    } catch (err) {
      const errorString = getErrorString(err);
      if (isNativeView) {
        postNativeAppAlbumIntroLayoutDesignerUpdateError(errorString);
      } else {
        openAlert({
          message: 'There was an error updating the slideshow.',
          info: errorString,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  if (!isOwner || !album) {
    return <FullScreenLoader />;
  }

  return (
    <IntroLayoutConfigurator
      forceOpen
      albumId={albumId}
      accountCardId={introLayout?.account_card?.id}
      onFinished={async ({ accountCard }) => {
        if (introLayout?.account_card?.id !== accountCard.id) {
          await onIntroLayoutUpdate({ accountCard });
        }

        if (isNativeView) {
          postNativeAppAlbumIntroLayoutDesignerDismiss();
        } else {
          openAlert({
            type: 'success',
            title: 'Your cover slide has been updated!',
          });
        }
      }}
      onClose={
        isNativeView ? postNativeAppAlbumIntroLayoutDesignerDismiss : undefined
      }
    />
  );
};

AlbumSlideshowIntroLayoutDesignerView.propTypes = propTypes;

const withMeQuery = graphql(MeQuery, {
  props: ({ data: { me, loading }, ownProps }) => ({
    loading: ownProps.loading || loading,
    user: me,
  }),
});

const mapStateToProps = ({ auth }) => ({
  authenticated: auth.authenticated,
});

export default compose(
  withRouter,
  withContributorPermissions(({ match }) => ({
    entityId: match.params.albumId,
  })),
  connect(mapStateToProps),
  withMeQuery,
)(AlbumSlideshowIntroLayoutDesignerView);
